/**
 * DirectorsComposer — Multi-agent cinematic prompt generator.
 *
 * Steps:
 *   1. `input`     → user writes/dictates the idea, submits to AD
 *   2. `questions` → AD asks up to 2 clarifying questions (optional)
 *   3. `pipeline`  → animated diagram of 6 agents (5 specialists + Director)
 *                    with SSE-driven state transitions (idle → running → done)
 *   4. `result`    → the Master Prompt is shown; user confirms and it's
 *                    injected into the invoking node
 *
 * Mounted once at App root. Opened via `useDirectorsStore.open({...})`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  X, Sparkles, Loader2, Mic, MicOff, ArrowRight, AlertTriangle,
  Check, Clapperboard, Camera, Package, Shirt, AudioLines, Scissors, Film,
  ChevronRight, RotateCcw, Copy, Coins,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDirectorsStore } from '../../store/directorsStore';
import { directorsService } from '../../services/directorsService';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition';
import { useCreditsStore } from '../../store/creditsStore';

// Static agent config for the diagram (order matters — matches backend)
const AGENTS = [
  { id: 'ad',        label: 'Asistente\nde Dirección', short: 'AD',        Icon: Clapperboard, phase: 1 },
  { id: 'dop',       label: 'Fotografía',              short: 'DoP',       Icon: Camera,       phase: 2 },
  { id: 'arte',      label: 'Arte',                    short: 'Arte',      Icon: Package,      phase: 2 },
  { id: 'vestuario', label: 'Vestuario',               short: 'Vest',      Icon: Shirt,        phase: 2 },
  { id: 'sonido',    label: 'Sonido',                  short: 'Sound',     Icon: AudioLines,   phase: 2 },
  { id: 'montaje',   label: 'Montaje',                 short: 'Mont',      Icon: Scissors,     phase: 2 },
  { id: 'director',  label: 'Director',                short: 'Direct',    Icon: Film,         phase: 3 },
];

const DirectorsComposer = () => {
  const { isOpen, nodeId, nodeType, currentPrompt, onInsert, close } = useDirectorsStore();

  const [step, setStep] = useState('input'); // input | questions | pipeline | result
  const [guion, setGuion] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Session tracking
  const sessionIdRef = useRef(null);

  // AD questions
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [guionEnriquecido, setGuionEnriquecido] = useState('');

  // Pipeline state
  const [agentStates, setAgentStates] = useState({});
  const [directorResult, setDirectorResult] = useState(null);
  const [creditsInfo, setCreditsInfo] = useState(null);
  const [creditsCharged, setCreditsCharged] = useState(null);
  const [pipelineGuion, setPipelineGuion] = useState(''); // guion_used from SSE
  const abortRef = useRef(null);
  const balance = useCreditsStore((s) => s.balance);
  const fetchBalance = useCreditsStore((s) => s.fetchBalance);

  // Mic (Web Speech API)
  const {
    supported: micSupported,
    listening: micListening,
    transcript: micTranscript,
    start: micStart,
    stop: micStop,
    reset: micReset,
  } = useSpeechRecognition({ lang: 'es-AR', interim: true });
  const micBaselineRef = useRef('');

  useEffect(() => {
    if (!micListening || !micTranscript) return;
    const base = micBaselineRef.current;
    const sep = base && !base.endsWith(' ') && !base.endsWith('\n') ? ' ' : '';
    setGuion(`${base}${sep}${micTranscript}`);
  }, [micTranscript, micListening]);

  // Reset when the modal closes/opens
  useEffect(() => {
    if (isOpen) {
      setStep('input');
      setGuion('');
      setQuestions([]);
      setAnswers({});
      setGuionEnriquecido('');
      setAgentStates({});
      setDirectorResult(null);
      setCreditsCharged(null);
      setPipelineGuion('');
      setErrorMsg(null);
      sessionIdRef.current = `directors-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      // Fetch the pipeline cost once per session so the CTA can display it.
      if (!creditsInfo) {
        directorsService.getCost().then(setCreditsInfo).catch(() => {});
      }
    } else {
      // On close: abort any in-flight pipeline + stop mic
      if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
      if (micListening) micStop();
    }
  }, [isOpen]);

  const toggleMic = () => {
    if (!micSupported) {
      toast.error('Tu navegador no soporta dictado por voz. Usá Chrome/Edge/Safari.');
      return;
    }
    if (micListening) { micStop(); return; }
    micBaselineRef.current = guion;
    micReset();
    micStart();
  };

  // ── Step 1: AD validation ────────────────────────────────
  const handleValidate = async (previousAnswers) => {
    setBusy(true);
    setErrorMsg(null);
    try {
      const res = await directorsService.validate({
        guion: guion.trim(),
        previousAnswers,
        sessionId: sessionIdRef.current,
      });
      if (res.estado === 'requiere_aclaracion') {
        setQuestions(res.preguntas_usuario || []);
        setAnswers({});
        setStep('questions');
      } else {
        setGuionEnriquecido(res.guion_enriquecido || guion);
        await handleStartPipeline(res.guion_enriquecido || guion);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Error validando la idea';
      setErrorMsg(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleAnswerSubmit = async () => {
    // Convert answers dict → array in question order
    const arr = questions.map((_, i) => (answers[i] || '').trim()).filter(Boolean);
    if (arr.length < questions.length) {
      toast.error('Respondé todas las preguntas.');
      return;
    }
    await handleValidate(arr);
  };

  // ── Step 3: Pipeline stream ──────────────────────────────
  const handleStartPipeline = async (guionEnriq) => {
    setStep('pipeline');
    // AD already finished successfully (validate returned 'listo_para_produccion').
    // Mark it as done up-front so the diagram reflects real state — but only
    // once the SSE actually starts producing events. See onEvent below.
    const { abort } = directorsService.executeStream(
      {
        guionEnriquecido: guionEnriq,
        nodeType: nodeType || 'generic',
        sessionId: sessionIdRef.current,
      },
      (evt, data) => {
        if (evt === 'pipeline_started') {
          setAgentStates((s) => ({ ...s, ad: { status: 'done' } }));
        } else if (evt === 'guion_used') {
          setPipelineGuion(data.guion_enriquecido || '');
        } else if (evt === 'agent_started') {
          setAgentStates((s) => ({ ...s, [data.agent]: { status: 'running' } }));
        } else if (evt === 'agent_completed') {
          setAgentStates((s) => ({
            ...s,
            [data.agent]: {
              status: data.ok ? 'done' : 'error',
              payload: data.payload,
              escalated: data.escalated || false,
            },
          }));
        } else if (evt === 'master_prompt') {
          setDirectorResult(data.director);
        } else if (evt === 'credits_deducted') {
          setCreditsCharged({ amount: data.amount, balanceAfter: data.balance_after, breakdown: data.breakdown });
          // Refresh the global balance store so top-nav updates.
          if (typeof fetchBalance === 'function') fetchBalance();
        } else if (evt === 'error') {
          const msg = data.message || 'Pipeline error';
          setErrorMsg(msg);
          toast.error(msg);
        } else if (evt === 'done') {
          setStep((prev) => (prev === 'pipeline' ? 'result' : prev));
        }
      }
    );
    abortRef.current = { abort };
  };

  // ── Step 4: Confirm & inject ─────────────────────────────
  const handleConfirm = () => {
    if (!directorResult?.master_prompt) return;
    if (typeof onInsert === 'function') {
      onInsert(directorResult.master_prompt);
    }
    toast.success('Master Prompt insertado en el nodo.');
    close();
  };

  const handleCopy = async () => {
    if (!directorResult?.master_prompt) return;
    try {
      await navigator.clipboard.writeText(directorResult.master_prompt);
      toast.success('Copiado al portapapeles.');
    } catch {
      toast.error('No se pudo copiar.');
    }
  };

  const handleRestart = () => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    setStep('input');
    setQuestions([]);
    setAnswers({});
    setGuionEnriquecido('');
    setAgentStates({});
    setDirectorResult(null);
    setErrorMsg(null);
  };

  const allDone = useMemo(
    () => AGENTS.every((a) => agentStates[a.id]?.status === 'done'),
    [agentStates]
  );

  if (!isOpen) return null;

  return (
    <div className="directors-backdrop" onClick={close}>
      <div
        className="directors-modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="directors-composer"
      >
        <div className="directors-bg" aria-hidden />

        <button className="directors-close" onClick={close} aria-label="Cerrar" data-testid="directors-close">
          <X size={16} />
        </button>

        <div className="directors-eyebrow">
          <Film size={13} /> DIRECTORS · PIPELINE CINEMATOGRÁFICO
        </div>

        {step === 'input' && (
          <div className="directors-hero">
            <h1 className="directors-headline">CONSTRUYE UN PROMPT DIGNO DE CINE</h1>
            <p className="directors-sub">
              Describí tu idea en lenguaje natural. Siete agentes (Asistente de Dirección,
              Fotografía, Arte, Vestuario, Sonido, Montaje y Director) la convertirán en
              un Master Prompt hiperpreciso, listo para inyectar en <b>{nodeType || 'tu nodo'}</b>.
            </p>

            <div className="directors-composer-bar">
              <div className="directors-textarea-wrap">
                <textarea
                  className="directors-textarea"
                  rows={4}
                  placeholder="Ej: un hombre solitario camina por un desierto al atardecer, cámara acompaña desde atrás, atmósfera melancólica…"
                  value={guion}
                  onChange={(e) => setGuion(e.target.value)}
                  autoFocus
                  data-testid="directors-guion-input"
                />
                <button
                  type="button"
                  className={`directors-mic-btn ${micListening ? 'listening' : ''} ${!micSupported ? 'unsupported' : ''}`}
                  onClick={toggleMic}
                  title={!micSupported ? 'Dictado no soportado' : (micListening ? 'Detener dictado' : 'Dictar por voz')}
                  data-testid="directors-mic-btn"
                >
                  {micListening ? <MicOff size={16} /> : <Mic size={16} />}
                  {micListening && <span className="mic-pulse" />}
                </button>
              </div>

              <button
                type="button"
                className="directors-cta"
                onClick={() => handleValidate()}
                disabled={busy || guion.trim().length < 6}
                data-testid="directors-start"
              >
                {busy ? (
                  <>
                    <Loader2 size={16} className="spin" />
                    <span>ANALIZANDO…</span>
                  </>
                ) : (
                  <>
                    <span>INICIAR PIPELINE</span>
                    <Sparkles size={16} />
                  </>
                )}
              </button>
            </div>

            {creditsInfo && (
              <div className="directors-cost-strip" data-testid="directors-cost-strip">
                <div className="directors-cost-main">
                  <Coins size={13} />
                  <span>
                    Costo por ejecución: <b>{creditsInfo.total} créditos</b>
                  </span>
                  {balance !== null && balance !== undefined && (
                    <span className="directors-cost-balance">
                      Balance actual: <b>{balance}</b>
                    </span>
                  )}
                </div>
                <div className="directors-cost-breakdown">
                  <span>AD × {creditsInfo.breakdown.ad}</span>
                  <span className="dot">·</span>
                  <span>5 esp × {creditsInfo.breakdown.specialists_each} = {creditsInfo.breakdown.specialists_total}</span>
                  <span className="dot">·</span>
                  <span>Director × {creditsInfo.breakdown.director}</span>
                </div>
              </div>
            )}

            {errorMsg && (
              <div className="directors-error"><AlertTriangle size={13} /> {errorMsg}</div>
            )}

            {currentPrompt && (
              <div className="directors-hint">
                <b>Prompt actual del nodo:</b> {currentPrompt.length > 200 ? `${currentPrompt.slice(0, 200)}…` : currentPrompt}
              </div>
            )}
          </div>
        )}

        {step === 'questions' && (
          <div className="directors-panel">
            <div className="directors-panel-head">
              <h2><Clapperboard size={16} /> El AD necesita precisar algo</h2>
              <p>Respondé con 1-2 frases cortas. El AD nunca inventa detalles que no dijiste.</p>
            </div>

            <div className="directors-questions">
              {questions.map((q, i) => (
                <div className="directors-q" key={i}>
                  <label>{i + 1}. {q}</label>
                  <input
                    type="text"
                    value={answers[i] || ''}
                    onChange={(e) => setAnswers((a) => ({ ...a, [i]: e.target.value }))}
                    placeholder="Tu respuesta…"
                    data-testid={`directors-answer-${i}`}
                  />
                </div>
              ))}
            </div>

            {errorMsg && <div className="directors-error"><AlertTriangle size={13} /> {errorMsg}</div>}

            <div className="directors-actions">
              <button className="directors-btn ghost" onClick={handleRestart}>← Reescribir idea</button>
              <button
                className="directors-btn primary"
                onClick={handleAnswerSubmit}
                disabled={busy}
                data-testid="directors-answers-submit"
              >
                {busy ? <><Loader2 size={14} className="spin" /> Procesando…</> : <><ArrowRight size={14} /> Continuar</>}
              </button>
            </div>
          </div>
        )}

        {(step === 'pipeline' || step === 'result') && (
          <div className="directors-pipeline-wrap">
            <div className="directors-pipeline-title">
              <span>Pipeline en ejecución</span>
              <span className="directors-node-badge">{nodeType || 'generic'}</span>
            </div>

            <div className="directors-diagram" data-testid="directors-diagram">
              {AGENTS.map((a, idx) => {
                const st = agentStates[a.id]?.status || 'idle';
                const escalated = agentStates[a.id]?.escalated;
                return (
                  <div className="directors-diagram-cell" key={a.id}>
                    <div
                      className={`directors-agent phase-${a.phase} status-${st}`}
                      data-agent={a.id}
                      data-status={st}
                    >
                      <div className="directors-agent-icon">
                        {st === 'running' ? <Loader2 size={18} className="spin" /> : <a.Icon size={18} />}
                      </div>
                      <div className="directors-agent-label">
                        {a.short}
                        {escalated && <span className="directors-agent-badge" title="Escaló a Llama-70B para asegurar JSON válido">70B</span>}
                      </div>
                      <div className="directors-agent-status">
                        {st === 'idle' && '·'}
                        {st === 'running' && 'procesando…'}
                        {st === 'done' && <><Check size={11} /> listo</>}
                        {st === 'error' && 'error'}
                      </div>
                    </div>
                    {idx < AGENTS.length - 1 && <ChevronRight size={14} className="directors-arrow" />}
                  </div>
                );
              })}
            </div>

            {pipelineGuion && (
              <details className="directors-guion-trace" data-testid="directors-guion-trace">
                <summary>
                  <Clapperboard size={12} />
                  <span>Guion que están recibiendo los agentes</span>
                  <span className="muted">({pipelineGuion.length} caracteres)</span>
                </summary>
                <p>{pipelineGuion}</p>
              </details>
            )}

            {errorMsg && <div className="directors-error"><AlertTriangle size={13} /> {errorMsg}</div>}

            {step === 'result' && directorResult && (
              <div className="directors-result">
                <div className="directors-result-section">
                  <label>Visión Directorial</label>
                  <p>{directorResult.vision_directorial}</p>
                </div>

                {directorResult.resolucion_conflictos && (
                  <div className="directors-result-section muted">
                    <label>Resolución de Conflictos</label>
                    <p>{directorResult.resolucion_conflictos}</p>
                  </div>
                )}

                <div className="directors-result-cols">
                  <div className="directors-result-section muted">
                    <label>Ejecución Visual</label>
                    <p>{directorResult.ejecucion_visual}</p>
                  </div>
                  <div className="directors-result-section muted">
                    <label>Ritmo & Sonido</label>
                    <p>{directorResult.ejecucion_ritmica_sonora}</p>
                  </div>
                </div>

                <div className="directors-master-prompt">
                  <div className="directors-master-head">
                    <label>Master Prompt</label>
                    <div className="directors-master-actions">
                      {creditsCharged && creditsCharged.amount > 0 && (
                        <span className="directors-charged-chip" data-testid="directors-charged">
                          <Coins size={11} />
                          Cobrado: {creditsCharged.amount}
                          {typeof creditsCharged.balanceAfter === 'number' && (
                            <span className="muted"> · Balance: {creditsCharged.balanceAfter}</span>
                          )}
                        </span>
                      )}
                      <button className="directors-btn ghost small" onClick={handleCopy} data-testid="directors-copy">
                        <Copy size={12} /> Copiar
                      </button>
                    </div>
                  </div>
                  <textarea
                    className="directors-master-textarea"
                    value={directorResult.master_prompt}
                    onChange={(e) => setDirectorResult((r) => ({ ...r, master_prompt: e.target.value }))}
                    rows={4}
                    data-testid="directors-master-prompt"
                  />
                </div>

                <div className="directors-actions">
                  <button className="directors-btn ghost" onClick={handleRestart} data-testid="directors-restart">
                    <RotateCcw size={13} /> Nueva idea
                  </button>
                  <button
                    className="directors-btn primary"
                    onClick={handleConfirm}
                    disabled={!directorResult?.master_prompt?.trim()}
                    data-testid="directors-confirm"
                  >
                    <Check size={14} /> Insertar en el nodo
                  </button>
                </div>
              </div>
            )}

            {step === 'pipeline' && !allDone && !errorMsg && (
              <div className="directors-pipeline-hint">
                <Loader2 size={13} className="spin" /> Los especialistas están procesando en paralelo…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DirectorsComposer;
